import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { attributeValueActions } from '../utils/Constants';
import Select from "react-select";
import debounce from 'lodash/debounce';
import { getUniqueAttributeValues } from '../services/RecategorizeServices';
import { customStyles } from '../utils/Styles';
import Table from 'Components/DesignSystem/Table/Table';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { TableColumn } from 'types/DesignSystem/Table';

type AttributeValuesMappingProps = {
    payload: TRecategorizePayload;
    setPayload: React.Dispatch<React.SetStateAction<TRecategorizePayload>>;
    recategorizationResponse: RecategorizationResponse | undefined;
    attributeValueData: AttributeValueMapping | null;
};

const AttributeValuesMapping = ({ payload, setPayload, recategorizationResponse, attributeValueData }: AttributeValuesMappingProps) => {
    const [hasMoreSearch, setHasMoreSearch] = useState(true);
    const [hasMoreNormal, setHasMoreNormal] = useState(true);
    const isMounted = useRef(true);
    const [allTargetValues, setAllTargetValues] = useState<AttributeValue[]>([]);
    const [filteredValues, setFilteredValues] = useState<AttributeValue[]>([]);
    const [normalPage, setNormalPage] = useState(1);
    const [searchPage, setSearchPage] = useState(1);
    const loadedPagesRef = useRef<Set<number>>(new Set([1]));
    const [isSearching, setIsSearching] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const sourceAttribute = useMemo(() =>
        payload?.unique_attr_source_list?.find(e => e.attribute_id === attributeValueData?.selectedSourceAttribute),
        [payload?.unique_attr_source_list, attributeValueData?.selectedSourceAttribute]
    );

    const targetAttribute = useMemo(() =>
        recategorizationResponse?.unique_attr_target_list?.find(e => e.attribute_id === attributeValueData?.selectedTargetAttribute),
        [recategorizationResponse?.unique_attr_target_list, attributeValueData?.selectedTargetAttribute]
    );

    // From payload - contains mapping info (action, target_attribute_value)
    const sourceAttributeValues = useMemo(() =>
        sourceAttribute?.unique_attribute_values ?? [],
        [sourceAttribute]
    );

    // From recategorizationResponse - original attribute values
    const originalSourceAttributeValues = useMemo(() =>
        recategorizationResponse?.unique_attr_source_list?.find(
            e => e.attribute_id === attributeValueData?.selectedSourceAttribute
        )?.attribute_value ?? [],
        [recategorizationResponse?.unique_attr_source_list, attributeValueData?.selectedSourceAttribute]
    );

    const shouldFetchInitialData = useCallback(() => {
        if (!attributeValueData?.selectedTargetAttribute) return false;
        const targetAttr = recategorizationResponse?.unique_attr_target_list?.find(
            attr => attr.attribute_id === attributeValueData.selectedTargetAttribute
        );
        return !targetAttr || !targetAttr.attribute_value || targetAttr.attribute_value.length === 0;
    }, [attributeValueData?.selectedTargetAttribute, recategorizationResponse?.unique_attr_target_list]);

    const debouncedLoadMoreOptions = useCallback(
        debounce(() => {
            const nextPage = isSearching ? searchPage + 1 : normalPage + 1;

            if (isSearching && hasMoreSearch) {
                fetchTargetValues(nextPage, searchValue);
            } else if (!isSearching && hasMoreNormal && !loadedPagesRef.current.has(nextPage)) {
                fetchTargetValues(nextPage, "");
            }
        }, 300),
        [hasMoreNormal, hasMoreSearch, normalPage, searchPage, isSearching, searchValue]
    );

    const fetchTargetValues = useCallback(async (pageNum: number, search: string) => {
        if (!attributeValueData?.selectedTargetAttribute || !isMounted.current) return

        try {
            const attributeValues = originalSourceAttributeValues.map(attrValue => attrValue.value)
            const formattedAttributeValueIds = JSON.stringify(attributeValues)

            const response = await getUniqueAttributeValues(
                attributeValueData.selectedTargetAttribute,
                pageNum,
                formattedAttributeValueIds,
                search
            )

            if (!response.data?.[0]?.attribute_values) return

            const attributeData = response.data[0]
            const newAttributeValues = attributeData.attribute_values

            if (search) {
                setFilteredValues(prev =>
                    pageNum === 1 ? newAttributeValues : [...prev, ...newAttributeValues]
                )
                setSearchPage(pageNum)
                setHasMoreSearch(pageNum < (attributeData?.totalPages || 1))
            } else {
                setAllTargetValues(prev => {
                    const uniqueValues = [...prev]
                    newAttributeValues.forEach((newVal: AttributeValue) => {
                        if (!uniqueValues.some(existing => existing.id === newVal.id)) {
                            uniqueValues.push(newVal)
                        }
                    })
                    return uniqueValues
                })
                loadedPagesRef.current.add(pageNum)
                setNormalPage(prevPage => Math.max(prevPage, pageNum))
                setHasMoreNormal(pageNum < (attributeData?.totalPages || 1))
            }
        } catch (error) {
            console.error("Error fetching attribute values:", error)
        }
    }, [attributeValueData?.selectedTargetAttribute, originalSourceAttributeValues])

    const resetSearchState = useCallback(() => {
        setFilteredValues([])
        setSearchPage(1)
        setHasMoreSearch(true)
        setIsSearching(false)
        setSearchValue("")
    }, [])

    const handleInputChange = useCallback(
        (input: string, { action }: { action: string }) => {
            if (action === "input-blur" || action === "menu-close") return

            if (input === "") {
                resetSearchState()
                // Don't reset normal page - continue from where it left off
                if (allTargetValues.length === 0) {
                    fetchTargetValues(normalPage, "")
                }
                return
            }

            setSearchValue(input)
            setIsSearching(true)
            setSearchPage(1)
            setHasMoreSearch(true)
            fetchTargetValues(1, input)
        },
        [fetchTargetValues, normalPage, resetSearchState]
    )

    const handleMenuScrollToBottom = useCallback(() => {
        if (isSearching && hasMoreSearch) {
            const nextSearchPage = searchPage + 1
            fetchTargetValues(nextSearchPage, searchValue)
        } else if (!isSearching && hasMoreNormal) {
            const nextNormalPage = normalPage + 1
            if (!loadedPagesRef.current.has(nextNormalPage)) {
                fetchTargetValues(nextNormalPage, "")
            }
        }
    }, [
        isSearching,
        hasMoreSearch,
        hasMoreNormal,
        searchPage,
        normalPage,
        searchValue,
        fetchTargetValues
    ])

    const handleMenuClose = useCallback(() => {
        if (!searchValue) return

        resetSearchState()
        if (allTargetValues.length === 0) {
            fetchTargetValues(normalPage, "")
        }
    }, [searchValue, normalPage, resetSearchState, allTargetValues.length])

    // Initial data fetch on component mount
    useEffect(() => {
        if (attributeValueData?.selectedTargetAttribute && allTargetValues.length === 0) {
            setNormalPage(1)
            loadedPagesRef.current = new Set([1])
            fetchTargetValues(1, "")
        }
    }, [attributeValueData?.selectedTargetAttribute, allTargetValues.length, fetchTargetValues])

    // Cleanup
    useEffect(() => {
        return () => {
            isMounted.current = false
            resetSearchState()
            setNormalPage(1)
            loadedPagesRef.current = new Set([1])
        }
    }, [resetSearchState])

    const debouncedSearch = useMemo(
        () =>
            debounce((input: string) => {
                setIsSearching(true);
                setSearchPage(1);
                setHasMoreSearch(true);
                setFilteredValues([]);
            }, 300),
        []
    );

    const getAvailableTargetValues = useCallback((currentValue: string) => {
        const alreadyMappedValues = new Set(
            sourceAttributeValues
                .filter(sourceVal =>
                    sourceVal.action === 'map' &&
                    sourceVal.target_attribute_value &&
                    sourceVal.attribute_value !== currentValue
                )
                .map(sourceVal => sourceVal.target_attribute_value)
        );

        const valuesToUse = isSearching ? filteredValues : allTargetValues;

        return valuesToUse
            .filter(val => !alreadyMappedValues.has(val.value))
            .map(val => ({
                label: val.value,
                value: val.value
            }));
    }, [sourceAttributeValues, allTargetValues, filteredValues, isSearching]);

    const handleAttributeValueActionChange = (attributeValue: string, action: AttributeValueActionType) => {
        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr => {
                if (attr.attribute_id !== attributeValueData?.selectedSourceAttribute) return attr;

                const existingUniqueValues = attr.unique_attribute_values || [];
                const valueExists = existingUniqueValues.some(val => val.attribute_value === attributeValue);
                const updatedUniqueValues = valueExists
                    ? existingUniqueValues.map(val =>
                        val.attribute_value === attributeValue
                            ? { ...val, action, target_attribute_value: undefined }
                            : val
                    )
                    : [...existingUniqueValues, { attribute_value: attributeValue, action, target_attribute_value: undefined }];

                return {
                    ...attr,
                    unique_attribute_values: updatedUniqueValues
                };
            });

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    const handleAttributeValueTargetChange = (attributeValue: string, targetValue: string) => {
        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr => {
                if (attr.attribute_id !== attributeValueData?.selectedSourceAttribute) return attr;

                const existingUniqueValues = attr.unique_attribute_values || [];
                const updatedUniqueValues = existingUniqueValues.map(val =>
                    val.attribute_value === attributeValue
                        ? { ...val, target_attribute_value: targetValue }
                        : val
                );

                return {
                    ...attr,
                    unique_attribute_values: updatedUniqueValues
                };
            });

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    useEffect(() => {
        return () => {
            isMounted.current = false;
            debouncedSearch.cancel();
            debouncedLoadMoreOptions.cancel();
            setSearchValue("");
            setNormalPage(1);
            setSearchPage(1);
            setHasMoreNormal(true);
            setHasMoreSearch(true);
        };
    }, []);

    useEffect(() => {
        const loadInitialData = async () => {
            if (shouldFetchInitialData()) {
                setAllTargetValues([]);
                setFilteredValues([]);
                setNormalPage(1);
                setSearchPage(1);
                loadedPagesRef.current = new Set();
                await fetchTargetValues(1, "");
            }
        };

        loadInitialData();
    }, [attributeValueData?.selectedTargetAttribute]);

    const selectProps = useMemo(() => ({
        id: "attributeValueId",
        className: "tw-outline-none",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: 'var(--ds-accent, #030562)'
            })
        },
        isSearchable: true,
        maxMenuHeight: 220,
        menuPlacement: "bottom" as const,
        menuPosition: "absolute" as const,
        // Escapes the DS Table wrapper's overflow box — see utils/Styles.tsx `menuPortal`.
        menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
        isClearable: false,
        placeholder: "Select Attribute Value"
    }), []);

    const actionTypeSelectProps = useMemo(() => ({
        id: "actionTypeId",
        className: "tw-outline-none",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: 'var(--ds-accent, #030562)'
            })
        },
        isSearchable: false,
        maxMenuHeight: 220,
        menuPlacement: "bottom" as const,
        menuPosition: "absolute" as const,
        // Escapes the DS Table wrapper's overflow box — see utils/Styles.tsx `menuPortal`.
        menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
        isClearable: false,
        placeholder: "Select"
    }), []);

    /*
     * The grid moved to DS `Table` (2026-09-13). Everything above this line — the
     * paged fetch, the search/normal page split, `loadedPagesRef` de-duplication, the
     * unmount cancels and both payload writers — is untouched; only the markup changed.
     */
    const columns = useMemo<TableColumn<UniqueAttributeValue>[]>(() => [
        {
            id: 'index',
            header: '#',
            accessor: 'attribute_value',
            width: '5%',
            cellType: 'numerical',
            render: (_v, _row, rowIndex) => rowIndex + 1,
        },
        {
            id: 'source',
            header: sourceAttribute?.attribute_name ?? '',
            accessor: 'attribute_value',
            width: '40%',
        },
        {
            id: 'action',
            header: 'Action',
            accessor: 'action',
            width: '20%',
            cellType: 'dropdown',
            render: (_v, row) => (
                /* Two enumerable options -> `Dropdown`, per the DS's own Dropdown-vs-
                   Combobox rule. `usePortal` because the cell is inside the Table's
                   overflow box. `handleAttributeValueActionChange` is untouched. */
                <div className="tw-pr-8">
                    <Dropdown
                        label=""
                        size="sm"
                        usePortal
                        placeholder="Select"
                        options={attributeValueActions}
                        value={row?.action ?? undefined}
                        onChange={(value) => handleAttributeValueActionChange(row.attribute_value, value as AttributeValueActionType)}
                        data-testid={`recategorize-value-action-${row.attribute_value}`}
                    />
                </div>
            ),
        },
        {
            id: 'target',
            header: targetAttribute?.attribute_name ?? '',
            accessor: 'target_attribute_value',
            width: '35%',
            cellType: 'dropdown',
            render: (_v, row) => (
                row?.action === 'create' ? row?.attribute_value : row.action === 'map' ? (
                    <div
                        title={row.target_attribute_value ?? ''}
                        className="tw-delay-0 tw-duration-0 tw-pr-8"
                    >
                        <Select<OptionType>
                            {...selectProps}
                            options={getAvailableTargetValues(row.attribute_value)}
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            onInputChange={handleInputChange}
                            onChange={(selectedOption) => {
                                if (selectedOption) {
                                    handleAttributeValueTargetChange(
                                        row.attribute_value,
                                        selectedOption.value
                                    );
                                }
                            }}
                            onMenuClose={handleMenuClose}
                            value={row.target_attribute_value
                                ? {
                                    value: row.target_attribute_value,
                                    label: row.target_attribute_value
                                }
                                : null
                            }
                            components={{
                                IndicatorSeparator: () => null,
                                NoOptionsMessage: () => (
                                    <div className="tw-p-2 tw-text-center tw-text-ink-tertiary">
                                        No attribute values available
                                    </div>
                                )
                            }}
                        />
                    </div>
                ) : (
                    "NA"
                )
            ),
        },
    ], [sourceAttribute?.attribute_name, targetAttribute?.attribute_name, actionTypeSelectProps, selectProps, getAvailableTargetValues, handleMenuScrollToBottom, handleInputChange, handleMenuClose]);

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-rounded-lg tw-my-4 tw-pb-6">
            <Table<UniqueAttributeValue>
                columns={columns}
                data={sourceAttributeValues}
                rowKey="attribute_value"
                striped
                bordered
                data-testid="recategorize-attribute-values-table"
                emptyContent={null}
            />
        </div>
    );
}

export default AttributeValuesMapping